/**
 * Common SweetAlert2 Service
 * Provides reusable alert methods for success, error, warning, info, and confirmation dialogs
 */

import Swal from 'sweetalert2'

/**
 * Default configuration for all alerts
 */
const defaultConfig = {
  confirmButtonColor: '#3b82f6', // Primary blue
  cancelButtonColor: '#ef4444', // Destructive red
  allowOutsideClick: false,
  allowEscapeKey: true,
  showCloseButton: true,
  width: '500px',
  padding: '2rem',
  customClass: {
    popup: 'rounded-lg shadow-xl',
    confirmButton: 'px-6 py-2 rounded-md font-semibold',
    cancelButton: 'px-6 py-2 rounded-md font-semibold',
    title: 'text-xl font-bold',
    htmlContainer: 'text-base',
  },
}

/**
 * Success Alert
 * @param title - Alert title
 * @param message - Alert message (optional)
 * @param timer - Auto close timer in milliseconds (optional, default: 3000)
 */
export const showSuccess = async (
  title: string,
  message?: string,
  timer?: number
): Promise<void> => {
  await Swal.fire({
    ...defaultConfig,
    icon: 'success',
    title,
    text: message,
    timer: timer || 3000,
    timerProgressBar: true,
    showConfirmButton: timer ? false : true,
    confirmButtonText: 'OK',
  })
}

/**
 * Error Alert
 * @param title - Alert title
 * @param message - Alert message (optional)
 * @param timer - Auto close timer in milliseconds (optional, default: 5000)
 */
export const showError = async (
  title: string,
  message?: string,
  timer?: number
): Promise<void> => {
  await Swal.fire({
    ...defaultConfig,
    icon: 'error',
    title,
    text: message,
    timer: timer || 5000,
    timerProgressBar: true,
    showConfirmButton: timer ? false : true,
    confirmButtonText: 'OK',
    confirmButtonColor: '#ef4444', // Red for errors
  })
}

/**
 * Warning Alert
 * @param title - Alert title
 * @param message - Alert message (optional)
 * @param timer - Auto close timer in milliseconds (optional)
 */
export const showWarning = async (
  title: string,
  message?: string,
  timer?: number
): Promise<void> => {
  await Swal.fire({
    ...defaultConfig,
    icon: 'warning',
    title,
    text: message,
    timer: timer || undefined,
    timerProgressBar: timer ? true : false,
    showConfirmButton: true,
    confirmButtonText: 'OK',
    confirmButtonColor: '#f59e0b', // Amber for warnings
  })
}

/**
 * Info Alert
 * @param title - Alert title
 * @param message - Alert message (optional)
 * @param timer - Auto close timer in milliseconds (optional, default: 4000)
 */
export const showInfo = async (
  title: string,
  message?: string,
  timer?: number
): Promise<void> => {
  await Swal.fire({
    ...defaultConfig,
    icon: 'info',
    title,
    text: message,
    timer: timer || 4000,
    timerProgressBar: true,
    showConfirmButton: timer ? false : true,
    confirmButtonText: 'OK',
    confirmButtonColor: '#3b82f6', // Blue for info
  })
}

/**
 * Confirmation Dialog
 * @param title - Dialog title
 * @param message - Dialog message (optional)
 * @param confirmText - Confirm button text (optional, default: 'Yes')
 * @param cancelText - Cancel button text (optional, default: 'No')
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export const showConfirm = async (
  title: string,
  message?: string,
  confirmText: string = 'Yes',
  cancelText: string = 'No'
): Promise<boolean> => {
  const result = await Swal.fire({
    ...defaultConfig,
    icon: 'question',
    title,
    text: message,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: '#3b82f6',
    cancelButtonColor: '#6b7280',
    reverseButtons: true, // Show cancel on left, confirm on right
  })

  return result.isConfirmed
}

/**
 * Delete Confirmation Dialog
 * @param itemName - Name of item to delete (optional)
 * @param message - Custom message (optional)
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export const showDeleteConfirm = async (
  itemName?: string,
  message?: string
): Promise<boolean> => {
  const defaultMessage = itemName
    ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
    : 'Are you sure you want to delete this item? This action cannot be undone.'

  return showConfirm(
    'Delete Confirmation',
    message || defaultMessage,
    'Yes, Delete',
    'Cancel'
  )
}

/**
 * Save Success Alert
 * @param isEditMode - Whether it's an edit operation (optional)
 * @param customMessage - Custom success message (optional)
 */
export const showSaveSuccess = async (
  isEditMode: boolean = false,
  customMessage?: string
): Promise<void> => {
  const title = isEditMode ? 'Updated Successfully!' : 'Saved Successfully!'
  const message = customMessage || (isEditMode ? 'Your changes have been saved.' : 'Your data has been saved successfully.')

  await showSuccess(title, message, 3000)
}

/**
 * Update Success Alert
 * @param customMessage - Custom success message (optional)
 */
export const showUpdateSuccess = async (customMessage?: string): Promise<void> => {
  await showSaveSuccess(true, customMessage)
}

/**
 * Post Success Alert
 * @param customMessage - Custom success message (optional)
 */
export const showPostSuccess = async (customMessage?: string): Promise<void> => {
  const message = customMessage || 'Posted successfully!'
  await showSuccess('Posted Successfully!', message, 3000)
}

/** JSON/string form of API error body (for console + fallback message). */
export function stringifyApiErrorBody(data: unknown): string {
  if (data == null || data === '') return ''
  if (typeof data === 'string') return data
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

/** Parse common API shapes: message, ProblemDetails, ModelState `errors`, GRN-style `result.msg`. */
export function extractApiErrorMessage(data: unknown): string {
  if (data == null || data === '') return ''
  if (typeof data === 'string') return data.trim()
  if (typeof data !== 'object') return String(data)
  const d = data as Record<string, unknown>

  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim()
  if (typeof d.error === 'string' && d.error.trim()) return d.error.trim()
  if (typeof d.title === 'string' && d.title.trim()) return d.title.trim()
  if (typeof d.detail === 'string' && d.detail.trim()) return d.detail.trim()

  if (d.result && typeof d.result === 'object') {
    const r = d.result as Record<string, unknown>
    if (r.isError === true && typeof r.msg === 'string' && r.msg.trim()) return r.msg.trim()
    if (typeof r.msg === 'string' && r.msg.trim()) return r.msg.trim()
  }

  if (d.errors && typeof d.errors === 'object') {
    const parts: string[] = []
    for (const [k, v] of Object.entries(d.errors as Record<string, unknown>)) {
      if (Array.isArray(v)) parts.push(`${k}: ${v.filter(Boolean).join(', ')}`)
      else if (v != null) parts.push(`${k}: ${String(v)}`)
    }
    if (parts.length) return parts.join('\n')
  }

  return ''
}

/**
 * Save Error Alert
 * @param error - Error object or message string
 * @param isEditMode - Whether it's an edit operation (optional)
 */
export const showSaveError = async (
  error: any,
  isEditMode: boolean = false
): Promise<void> => {
  const title = isEditMode ? 'Update Failed' : 'Save Failed'
  const defaultMsg = isEditMode ? 'Failed to update. Please try again.' : 'Failed to save. Please try again.'
  const data = error?.response?.data
  const status = error?.response?.status
  const bodyStr = stringifyApiErrorBody(data)

  console.error(
    `%c[API Save Error] HTTP ${status ?? '?'}`,
    'font-weight:bold;color:#b91c1c',
    '\nResponse body:',
    bodyStr || '(empty)',
  )

  let message = extractApiErrorMessage(data)
  if (!message && bodyStr) {
    message =
      bodyStr.length > 1200
        ? `${bodyStr.slice(0, 1200)}…\n\n(Full response body is in the console: [API Save Error])`
        : bodyStr
  }
  if (!message) {
    message = typeof error?.message === 'string' && error.message ? error.message : defaultMsg
  }

  await showError(title, message, 8000)
}

/**
 * Post Error Alert
 * @param error - Error object or message string
 */
export const showPostError = async (error: any): Promise<void> => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Failed to post. Please try again.'

  await showError('Post Failed', message, 5000)
}

/**
 * Validation Error Alert
 * @param message - Validation error message
 */
export const showValidationError = async (message: string): Promise<void> => {
  await showError('Validation Error', message, 4000)
}

/**
 * Loading Alert (for async operations)
 * @param title - Loading title
 * @param message - Loading message (optional)
 * @returns Swal instance for closing
 */
export const showLoading = (title: string = 'Loading...', message?: string) => {
  return Swal.fire({
    title,
    text: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading()
    },
  })
}

/**
 * Close any open Swal alert
 */
export const closeSwal = (): void => {
  Swal.close()
}

